import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  SxProps,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { FormikProps } from 'formik';
import { useMemo, useState, useCallback, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useAtomValue } from 'jotai';
import { KpiFormValues, WorkpackLogs, WorklogsField, WorklogsSection } from '../../types';
import {
  convertValueByUnit,
  getLoggedValueForField,
  getSectionUnitLabel,
  getSectionUnitSymbol,
} from '../../utils';
import * as globalState from '@/components/Analysis/state';
import {
  accordionStyles,
  getAccordionSummaryStyles,
  headerCellStyles,
  bodyCellStyles,
  inputCellStyles,
  textFieldStyles,
  tooltipStyles,
  accordianDetailsStyles,
  remarksScrollContainerSx,
  remarksTextFieldSx,
} from './styles';
import { TotalSumRow } from '../KpiTotalSumRow';
import { DiscrepancyReasonField } from '../DiscrepancyReasonField';
import { SectionUnitsEnum, TemplateFieldTypeEnum } from '@/__generated__/graphql';
import { KPI_NUMBER_OF_DAILY_HOURS } from '../../constants';
import { KpiExpectedTotalHours } from './KpiExpectedTotalHours';
import { Banner } from '@/components/shared/Banner';
import { KpiCommentTableCell } from './KpiCommentTableCell';
import { abyssColors } from '@/theme/colors';
import { roundNumber } from '@/utils/unitSystem';

type Props = {
  section: WorklogsSection;
  fields: WorklogsField[];
  workpackLogs: WorkpackLogs | undefined;
  logsLoading: boolean;
  formik: FormikProps<KpiFormValues>;
  isSubmitting: boolean;
};

export const KpiPanelSection = ({
  section,
  fields,
  workpackLogs,
  logsLoading,
  formik,
  isSubmitting,
}: Props) => {
  const [expanded, setExpanded] = useState(section.defaultOpen ?? true);
  const pob = useMemo(() => {
    const val = formik.values.pob?.value;
    const numericVal = Number(val);
    return isNaN(numericVal) ? 0 : numericVal;
  }, [formik.values?.pob?.value]);

  const sectionFields = useMemo(
    () =>
      section.fieldKeys
        .map((fieldKey) => fields.find((field) => field.key === fieldKey))
        .filter((field) => section.fieldKeys.includes(field?.key || '')),
    [fields, section.fieldKeys]
  );

  const textareaFields = useMemo(
    () =>
      sectionFields.filter(
        (field): field is WorklogsField => field?.type === TemplateFieldTypeEnum.Textarea
      ),
    [sectionFields]
  );
  const isTextareaSection = textareaFields.length > 0;

  const unitSystem = useAtomValue(globalState.unitSystem);
  const unitLabel = getSectionUnitLabel(section.sectionUnit);
  const unitSymbol = getSectionUnitSymbol(section.sectionUnit, unitSystem);

  const isAreaSection = section.sectionUnit === SectionUnitsEnum.Area;

  const sectionAreaViewDecimals = useMemo(() => {
    if (sectionFields.length === 0) {
      return 3;
    }
    return Math.max(...sectionFields.map((field) => (field ? field.roundToDecimal ?? 3 : 3)));
  }, [sectionFields]);

  const getFieldDisplayValue = useCallback(
    (value: number, field: WorklogsField): number => {
      const decimals = field.roundToDecimal ?? 3;
      if (section.sectionUnit === SectionUnitsEnum.Area) {
        return convertValueByUnit(value, section.sectionUnit, unitSystem, 'view', {
          viewRoundDecimals: decimals,
        });
      }
      const converted = convertValueByUnit(value, section.sectionUnit, unitSystem, 'view');
      return roundNumber(converted, decimals);
    },
    [section.sectionUnit, unitSystem]
  );

  const handleFieldChange = (fieldKey: string, value: string) => {
    const currentField = formik.values[fieldKey];
    formik.setFieldValue(fieldKey, {
      ...currentField,
      value: value === '' ? '' : Number(value),
    });
  };

  // Calculate totals (area: sum stored m² then convert once for display; other units: sum per-field display values)
  const totalLogged = useMemo(() => {
    if (isAreaSection) {
      const sumSquareMeters = sectionFields.reduce((sum, field) => {
        if (!field) {
          return sum;
        }
        return sum + getLoggedValueForField(field.key, workpackLogs);
      }, 0);
      return convertValueByUnit(sumSquareMeters, section.sectionUnit, unitSystem, 'view', {
        viewRoundDecimals: sectionAreaViewDecimals,
      });
    }
    return sectionFields.reduce((sum, field) => {
      if (field) {
        const loggedValue = getLoggedValueForField(field.key, workpackLogs);
        return sum + getFieldDisplayValue(loggedValue, field);
      }
      return sum;
    }, 0);
  }, [
    isAreaSection,
    sectionFields,
    workpackLogs,
    section.sectionUnit,
    unitSystem,
    sectionAreaViewDecimals,
    getFieldDisplayValue,
  ]);

  const totalFormValue = useMemo(() => {
    if (isAreaSection) {
      const sumSquareMeters = sectionFields.reduce((sum, field) => {
        if (!field?.key) {
          return sum;
        }
        const formValue = formik.values[field.key]?.value;
        if (typeof formValue !== 'number') {
          return sum;
        }
        return sum + convertValueByUnit(formValue, section.sectionUnit, unitSystem, 'update');
      }, 0);
      return convertValueByUnit(sumSquareMeters, section.sectionUnit, unitSystem, 'view', {
        viewRoundDecimals: sectionAreaViewDecimals,
      });
    }
    return sectionFields.reduce((sum, field) => {
      if (field && field.key) {
        const fieldKey = field.key;
        const formValue = formik.values[fieldKey]?.value;
        return sum + (typeof formValue === 'number' ? formValue : 0);
      }
      return sum;
    }, 0);
  }, [
    isAreaSection,
    sectionFields,
    formik.values,
    section.sectionUnit,
    unitSystem,
    sectionAreaViewDecimals,
  ]);

  const isHoursSection = section.sectionUnit === SectionUnitsEnum.Hours;

  const totalCalculatedValue = isHoursSection ? totalFormValue * pob : totalFormValue;

  const discrepancyInfo = useMemo(() => {
    if (!isHoursSection) return;

    const maximumHours = KPI_NUMBER_OF_DAILY_HOURS * pob;
    if (maximumHours === 0 || typeof maximumHours !== 'number') return;

    if (totalCalculatedValue === maximumHours) return;

    const difference = Math.abs(totalCalculatedValue - maximumHours);
    const isOver = totalCalculatedValue > maximumHours;

    return {
      shouldShow: true,
      difference,
      isOver,
      message: `Logged hours are ${difference} hour${difference !== 1 ? 's' : ''} ${
        isOver ? 'higher' : 'lower'
      } than expected, please provide a reason`,
    };
  }, [isHoursSection, pob, totalCalculatedValue]);

  const disableAllFields = (() => {
    // Only need this on 'Hours' sections
    if (!isHoursSection) return false;
    // User must enter a POB before entering any hours
    if (!pob) return true;
    // This shouldn't happen but just in case...
    if (pob < 0) return true;
    return false;
  })();

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        disableGutters
        sx={accordionStyles}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={getAccordionSummaryStyles(expanded)}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, color: abyssColors.primary[500], fontSize: '1.4rem' }}
            >
              {section.title}
            </Typography>
            {isHoursSection && pob > 0 && <KpiExpectedTotalHours pob={pob} />}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={accordianDetailsStyles}>
          {sectionFields.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              No fields found for this section
            </Box>
          )}
          {sectionFields.length > 0 && isTextareaSection && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
              {textareaFields.map((field) => {
                const fieldKey = field.key ?? '';
                const formValue = formik.values[fieldKey]?.value;
                const textValue = typeof formValue === 'string' ? formValue : '';
                return (
                  <TextareaField
                    key={field.id}
                    field={field}
                    value={textValue}
                    onChange={(key, value) =>
                      formik.setFieldValue(key, { ...formik.values[key], value })
                    }
                    onBlur={formik.handleBlur}
                    disabled={isSubmitting}
                  />
                );
              })}
            </Box>
          )}
          {sectionFields.length > 0 && !isTextareaSection && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...headerCellStyles,
                        width: isHoursSection ? '25%' : '32%',
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        ...headerCellStyles,
                        width: isHoursSection ? '22%' : '28%',
                      }}
                    >
                      Total Logged {unitLabel ?? ''}
                      {unitSymbol ? ` (${unitSymbol})` : ''} Till Selected Date
                    </TableCell>
                    {isHoursSection && (
                      <TableCell sx={{ ...headerCellStyles, width: '22%' }}>
                        Calculated Total POB {unitLabel ?? ''}
                        {unitSymbol ? ` (${unitSymbol})` : ''}
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        ...headerCellStyles,
                        width: isHoursSection ? '21%' : '30%',
                      }}
                    >
                      {isHoursSection
                        ? `Individual ${unitLabel ?? ''}${unitSymbol ? ` (${unitSymbol})` : ''}`
                        : `${unitLabel ?? ''}${
                            unitSymbol ? ` (${unitSymbol})` : ''
                          } Logged On Selected Date`}
                    </TableCell>
                    <TableCell sx={{ ...headerCellStyles, width: '10%', textAlign: 'center' }}>
                      Comment
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sectionFields.map((field) => {
                    const fieldKey = field?.key || '';
                    const touched = formik.touched[fieldKey];
                    const fieldError = formik.errors[fieldKey];
                    // Access the nested value error
                    const error =
                      typeof fieldError === 'object' && fieldError && 'value' in fieldError
                        ? String(fieldError.value || '')
                        : undefined;
                    const showError = touched && !!error;

                    if (field) {
                      const rawFormVal = formik.values[fieldKey]?.value;
                      const formVal: number | '' = typeof rawFormVal === 'number' ? rawFormVal : '';
                      return (
                        <FieldRow
                          key={field?.id}
                          field={field}
                          section={section}
                          loggedValue={getFieldDisplayValue(
                            getLoggedValueForField(field?.key, workpackLogs),
                            field
                          )}
                          formValue={formVal}
                          calculatedValue={
                            isHoursSection && typeof formVal === 'number' ? formVal * pob : ''
                          }
                          isHoursSection={isHoursSection}
                          isLoading={logsLoading}
                          onChange={handleFieldChange}
                          onBlur={formik.handleBlur}
                          disabled={isSubmitting || disableAllFields}
                          error={showError ? error : undefined}
                        />
                      );
                    }

                    return <></>;
                  })}

                  {/* Only need to show total sum row for hours section */}
                  {isHoursSection && (
                    <TotalSumRow
                      unitLabel={unitLabel}
                      loading={logsLoading}
                      totalLogged={totalLogged}
                      totalFormValue={totalFormValue}
                      totalCalculatedValue={totalCalculatedValue}
                      isError={discrepancyInfo?.shouldShow}
                    />
                  )}
                </TableBody>
              </Table>

              {/* Show a warning banner if POB is not set with an hours section */}
              {isHoursSection && !pob && (
                <Box sx={{ mt: 1 }}>
                  <Banner
                    message={'Set POB from the top to calculate total hours.'}
                    severity="warning"
                  />
                </Box>
              )}

              <DiscrepancyReasonField
                sectionKey={section.key}
                sx={{ mt: 1 }}
                helperText={discrepancyInfo?.message}
                isError={!!discrepancyInfo?.shouldShow}
                isPobEmpty={!pob}
              />
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

type TextareaFieldProps = {
  field: WorklogsField;
  value: string;
  onChange: (fieldKey: string, value: string) => void;
  onBlur: (e: React.FocusEvent) => void;
  disabled: boolean;
};

const TextareaField = ({ field, value, onChange, onBlur, disabled }: TextareaFieldProps) => {
  const scrollContainerReference = useRef<HTMLDivElement | null>(null);
  const inputReference = useRef<HTMLTextAreaElement | null>(null);
  const fieldKey = field.key ?? '';

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const target = event.target;
      const cursorAtEnd = (target.selectionEnd ?? 0) === target.value.length;
      onChange(fieldKey, target.value);
      if (cursorAtEnd) {
        requestAnimationFrame(() => {
          const container = scrollContainerReference.current;
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
    },
    [fieldKey, onChange]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="body2" sx={{ color: abyssColors.primary[500], fontWeight: 500 }}>
        {field.name}
      </Typography>
      <Box ref={scrollContainerReference} sx={remarksScrollContainerSx}>
        <TextField
          multiline
          fullWidth
          name={fieldKey}
          id={fieldKey}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={field.fieldDescription ?? ''}
          disabled={disabled}
          variant="standard"
          slotProps={{ input: { disableUnderline: true } }}
          inputRef={inputReference}
          sx={remarksTextFieldSx}
        />
      </Box>
    </Box>
  );
};

type FieldRowProps = {
  field: WorklogsField;
  section: WorklogsSection;
  loggedValue: number;
  formValue: number | '';
  calculatedValue: number | '';
  isHoursSection: boolean;
  isLoading: boolean;
  onChange: (fieldKey: string, value: string) => void;
  onBlur: (e: React.FocusEvent) => void;
  disabled?: boolean;
  error?: string;
};

const FieldRow = ({
  field,
  section,
  loggedValue,
  formValue,
  calculatedValue,
  isHoursSection,
  isLoading,
  onChange,
  onBlur,
  disabled,
  error,
}: FieldRowProps) => {
  return (
    <TableRow sx={{ border: 1, borderColor: 'divider' }}>
      <TableCell
        sx={{
          ...bodyCellStyles,
          color: 'text.primary',
          border: 1,
          borderRight: 0,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {field.name}
          {field.fieldDescription && (
            <Tooltip title={field.fieldDescription} arrow placement="top" slotProps={tooltipStyles}>
              <InfoIcon fontSize="small" sx={{ cursor: 'pointer' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>

      <TableCell
        sx={{
          ...bodyCellStyles,
          color: 'text.primary',
          border: 1,
          borderColor: 'divider',
          borderRight: isHoursSection
            ? '1px solid #E0E0E0 !important'
            : '1px solid #7E9BBC !important',
          borderLeft: '1px solid #E0E0E0 !important',
        }}
      >
        {isLoading ? <Skeleton variant="text" /> : loggedValue}
      </TableCell>

      {isHoursSection && (
        <TableCell
          sx={{
            ...bodyCellStyles,
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            borderLeft: '1px solid #E0E0E0 !important',
            borderRight: '1px solid #7E9BBC !important',
          }}
        >
          {isLoading ? (
            <Skeleton variant="text" />
          ) : calculatedValue === '' ? (
            '--'
          ) : (
            calculatedValue
          )}
        </TableCell>
      )}

      <TableCell sx={{ ...bodyCellStyles, ...inputCellStyles } as SxProps}>
        <TextField
          size="small"
          type="number"
          variant="outlined"
          placeholder="0"
          fullWidth
          name={field.key || ''}
          id={field.key || ''}
          value={formValue}
          onChange={(e) => field.key && onChange(field.key, e.target.value)}
          onBlur={onBlur}
          slotProps={{
            htmlInput: { min: 0, 'aria-describedby': error ? `${field.key}-error` : undefined },
          }}
          sx={textFieldStyles}
          disabled={disabled}
          error={!!error}
          helperText={error}
          aria-invalid={!!error}
        />
      </TableCell>
      <KpiCommentTableCell field={field} section={section} />
    </TableRow>
  );
};
